import {
  Box, Button, Grid, SelectChangeEvent, Typography,
} from '@mui/material';
import React from 'react';
import csvDownload from 'json-to-csv-export';
import { useQuery, useLazyQuery } from '@apollo/client';
import EmptyPageMessage from './EmptyPageMessage';
import { colors } from '../../theme';
import DateFilter from '../DateFilter';
import FeedbackFiltersDialog from '../FeedbackFiltersDialog';
import SurveysGraphsLoader from './SurveysGraphsLoader';
import SurveyResponseCard from './SurveyResponseCard';
import { GET_FILTERED_TYPEFORM_RESPONSES } from '../../queries';

function DayTenTab({
  awoId, typeformIds, careType: careTypeString, handleCardClick,
}: {
  awoId: string, typeformIds: string[], careType: string, handleCardClick: (id: number) => void,
}) {
  const [daysAgo, setDaysAgo] = React.useState(-1);
  const [downloading, setDownloading] = React.useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState<Record<string, boolean>>({
    dogs: true,
    cats: true,
    adopted: careTypeString === 'Adopted',
    fostered: careTypeString === 'Foster',
  });
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);

  // Map appliedFilters to careType and animalType
  const mappedFilters = React.useMemo(() => {
    let careType = null;
    let animalType = null;

    // Map species filters to careType
    if (appliedFilters.dogs && !appliedFilters.cats) {
      animalType = 'Dog';
    } else if (appliedFilters.cats && !appliedFilters.dogs) {
      animalType = 'Cat';
    }
    // If both or neither are checked, careType remains null

    // Map relationship filters to animalType
    if (appliedFilters.adopted && !appliedFilters.fostered) {
      careType = 'Adopted';
    } else if (appliedFilters.fostered && !appliedFilters.adopted) {
      careType = 'Foster';
    }
    // If both or neither are checked, animalType remains null

    return { careType, animalType };
  }, [appliedFilters]);

  const {
    loading,
    data: typeformData,
    fetchMore,
    refetch,
  } = useQuery(GET_FILTERED_TYPEFORM_RESPONSES, {
    variables: {
      typeformIds,
      awoId,
      limit: 6,
      offset: 0,
      orderBy: 'desc',
      daysAgo,
      careType: mappedFilters.careType,
      animalType: mappedFilters.animalType,
    },
    skip: !awoId,
  });

  const [downloadQuery, { loading: downloadLoading }] = useLazyQuery(GET_FILTERED_TYPEFORM_RESPONSES);

  // Refetch when daysAgo or appliedFilters changes
  React.useEffect(() => {
    if (awoId) {
      setOffset(0);
      setHasMore(true);
      refetch({
        typeformIds,
        awoId,
        limit: 6,
        offset: 0,
        daysAgo,
        orderBy: 'desc',
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
      });
    }
  }, [daysAgo, appliedFilters, awoId, refetch, typeformIds, mappedFilters]);

  React.useEffect(() => {
    if (typeformData?.responses.length < 6) {
      setHasMore(false);
    }
  }, [typeformData]);

  const handleShowMore = () => {
    const newOffset = offset + 6;
    fetchMore({
      variables: {
        typeformIds,
        awoId,
        offset: newOffset,
        limit: 6,
        daysAgo,
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || fetchMoreResult.responses.length < 6) {
          setHasMore(false);
        }
        setOffset(newOffset);

        return {
          responses: [
            ...prev.responses,
            ...fetchMoreResult.responses,
          ],
        };
      },
    });
  };

  const downloadHandler = () => {
    // Question title normalization mapping
    const normalizeQuestion = (question: string) => {
      let mappedQuestion = null;
      if (question.startsWith('On a scale of 1-5')) {
        mappedQuestion = 'On a scale from 1-5, how are things going?';
      } else if (question.startsWith('What\'s gone well with')) {
        mappedQuestion = 'What\'s gone well with your pet?';
      } else if (question.startsWith('Do you have any behavior concerns so far?')
          || question.startsWith('Have you experienced any challenges')) {
        mappedQuestion = 'Have you experienced any challenges?';
      } else if (question.startsWith('What are your behavior concerns?')
          || question.startsWith('What challenges have you experienced?')) {
        mappedQuestion = 'What challenges have you experienced?';
      } else if (question.startsWith('Last question!')) {
        mappedQuestion = 'Did you rename your pet?';
      } else if (question.startsWith('What\'s their new name?')) {
        mappedQuestion = 'What is their new name?';
      }
      // Add more conditions as needed
      return mappedQuestion;
    };

    downloadQuery({
      variables: {
        typeformIds,
        awoId,
        daysAgo,
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
        offset: 0,
        orderBy: 'desc',
      },
    }).then((result) => {
      const downloadData = result.data.responses.map((response: any) => {
        const baseData = {
          Email: response.petParent.email,
          'Animal Name': response.animal?.name || response.petParent.animals.slice(0, 3).map((animal: any) => animal.name).join(', '),
          'Animal Species': response.animal?.animalType || response.petParent.animals[0].animalType,
          'Animal Care Type': response.animal?.careType || response.petParent.animals[0].careType,
          'Date of Submission': response.submittedAt,
        };

        // Define the order you want questions to appear in CSV
        const questionOrder = careTypeString === 'Foster'
          ? [
              'On a scale from 1-5, how are things going?',
              'What\'s gone well with your pet?',
              'Have you experienced any challenges?',
              'What challenges have you experienced?',
            ]
          : [
              'On a scale from 1-5, how are things going?',
              'What\'s gone well with your pet?',
              'Have you experienced any challenges?',
              'What challenges have you experienced?',
              'Did you rename your pet?',
              'What is their new name?',
            ];

        // Flatten answers into individual columns with normalized titles
        const answerData = response.answers.reduce((acc: any, answer: any) => {
          const normalizedQuestion = normalizeQuestion(answer.question);
          if (normalizedQuestion) {
            let value = '';
            if (answer.text) {
              if (answer.text.toLowerCase() === 'true') {
                value = 'Yes';
              } else if (answer.text.toLowerCase() === 'false') {
                value = 'No';
              } else {
                value = answer.text;
              }
            }
            acc[`"${normalizedQuestion}"`] = value;
          }
          return acc;
        }, {});

        // Create ordered object with questions in specified order
        const orderedAnswerData: Record<string, any> = {};
        questionOrder.forEach((question) => {
          orderedAnswerData[`"${question}"`] = answerData[`"${question}"`] || '';
        });

        return { ...baseData, ...orderedAnswerData };
      });

      csvDownload({
        data: downloadData,
        filename: `Day_10_Surveys_${new Date().toISOString()}.csv`,
      });
      setDownloading(false);
    }).catch((error) => {
      console.error('Download error:', error);
      setDownloading(false);
    });
  };

  return (
    !loading && typeformData?.responses.length === 0 ? (
      <EmptyPageMessage />
    ) : (
      <>
        <Box sx={{
          display: 'flex',
          gap: 1,
          background: colors.grey['50'],
          px: 2.38,
          py: 0.5,
          }}
        >
          <DateFilter
            formControl={{
              variant: 'standard',
              sx: { bgcolor: 'transparent' },
            }}
            selectedDate={daysAgo}
            onChange={(e: SelectChangeEvent<number>) => {
              setDaysAgo(Number(e.target.value));
            }}
          />
          <Button
            variant="text"
            size="small"
            color="black"
            onClick={() => setFilterDialogOpen(true)}
          >
            Filters
          </Button>
          <Button
            disabled={downloadLoading}
            onClick={downloadHandler}
            variant="text"
            size="small"
            color="black"
          >
            Download this data
          </Button>
        </Box>
        <Box
          sx={{
            px: 3,
          }}
        >
          {
            (!awoId || loading) && <SurveysGraphsLoader />
          }
          {
            !loading && (
              <>
                <Box
                  sx={{
                    py: 2,
                  }}
                >
                  <Typography variant="h5">{careTypeString === 'Adopted' ? 'Day 10 Adopter Surveys' : 'Day 10 Foster Surveys'}</Typography>
                  <Typography sx={{ mt: 3, color: colors.secondary.text }} variant="body2">
                    {careTypeString === 'Adopted'
                      ? 'Ten days after adoption, we ask adopters to complete a brief survey'
                      : 'Ten days after an animal enters the home, we ask foster parents to complete a brief survey'}
                    . Their responses help us deliver relevant resources.
                    Results can be filtered by time period and species, and are available for download.
                  </Typography>
                </Box>
                <Box sx={{ mx: -1 }}>
                  <Grid container spacing={2} sx={{ py: 2 }}>
                    {typeformData?.responses.map((response) => (
                      <Grid
                        item
                        xs={6}
                        lg={4}
                        key={response.id}
                      >
                        <SurveyResponseCard
                          response={response}
                          onClick={() => handleCardClick(response.petParent.id)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )
          }
        </Box>
        {!loading && typeformData?.responses.length > 0 && hasMore && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              type="button"
              variant="text"
              onClick={handleShowMore}
            >
              Show more
            </Button>
          </Box>
        )}
        <FeedbackFiltersDialog
          open={filterDialogOpen}
          setOpen={setFilterDialogOpen}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          showCareType={false}
          showAnimalType
        />
      </>
    )
  );
}

export default DayTenTab;
